import type { Editor } from "@tiptap/react";
import type { LucideIcon } from "lucide-react";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import ColorPickerWrapper from "@v1/components/common/designSystem/color/ColorPickerWrapper";
import SolidColorPicker from "@v1/components/common/designSystem/color/SolidColorPicker";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { usePrevious } from "@v1/hooks/usePrevious";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import tinycolor from "tinycolor2";

const DEFAULT_COLOR = {
  color: tinycolor("black").toRgbString(),
  background: tinycolor("white").toRgbString(),
};

const TipTapToolbarColorPicker: React.FC<
  React.PropsWithChildren<{
    editor: Editor | null;
    attributeName: "color" | "background";
    onUpdateColor(color: string | null): void;
    icon: LucideIcon;
    isActive: boolean;
  }>
> = ({ editor, attributeName, onUpdateColor, icon: Icon, isActive }) => {
  const [isVisible, setIsVisible] = React.useState(false);

  // Note (Noah, 2022-11-13): Because when we call onUpdateColor, we run the command in
  // TipTap which moves focus to the tiptap editor, onInteractOutside for the popover will
  // always be called, which means the popover will immediately dismiss. To avoid this, we
  // keep a ref which tracks whether an update is in progress, and if it is we preventDefault
  // in the onInteractOutside to keep the popover open. I'm not sure if setTimeout is the
  // best way to do this, but I couldn't figure out a different way
  const isUpdatingColorRef = React.useRef(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const onChangeColor = (color: string | null) => {
    isUpdatingColorRef.current = true;
    onUpdateColor(color);
    // Note (Noah, 2022-11-13): If we just updated the color 200ms ago, make sure
    // to clear the pending timeout since we don't want the isUpdatingColorRef to
    // get set to false before this event finishes processing
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      isUpdatingColorRef.current = false;
    }, 200);
  };

  const prevVisible = usePrevious(isVisible);
  const instantColor =
    attributeName === "color"
      ? editor?.getAttributes("textStyle").color
      : editor?.getAttributes("highlight").color;

  const color =
    (isVisible && prevVisible !== isVisible && instantColor) ||
    DEFAULT_COLOR[attributeName];

  const isTextColorPopover = attributeName === "color";
  const title = isTextColorPopover
    ? "Configure Text Color"
    : "Configure Highlight Color";

  return (
    <ColorPickerWrapper
      isPopoverOpen={isVisible}
      onOpenPopoverChange={setIsVisible}
      onInteractOutside={(e) => {
        // Note (Noah, 2022-11-13): Don't dismiss the popover if we're currently updating
        // (this onInteractOutside might have been triggered by the onUpdateColor call, which
        // focuses the Tiptap editor)
        if (isUpdatingColorRef.current) {
          e.preventDefault();
        }
      }}
      popoverTitle={title}
      trigger={
        <Tooltip
          triggerAsChild
          content={exhaustiveSwitch({ type: attributeName })({
            color: "Font Color",
            background: "Background Color",
          })}
        >
          <button
            className={twMerge(
              "flex items-center justify-center bg-transparent p-0",
              isActive && "bg-primary rounded-xs text-white",
            )}
          >
            <Icon size={16} />
          </button>
        </Tooltip>
      }
      content={
        <div className="flex flex-col gap-2">
          <SolidColorPicker
            value={color}
            onChange={(color) => {
              if (color) {
                onChangeColor(color);
              }
            }}
          />
          <div className="flex justify-end">
            <DeprecatedButton
              variant="primary"
              onClick={() => {
                onChangeColor(null);
                setIsVisible(false);
              }}
            >
              Reset
            </DeprecatedButton>
          </div>
        </div>
      }
    />
  );
};

export default TipTapToolbarColorPicker;
